import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, RadialBarChart, RadialBar, PolarAngleAxis } from 'recharts';
const CertificationComplianceDashboard: React.FC = () => {
  const kpis = [{
    title: 'IATF Readiness %',
    value: '92%',
    change: '+5%',
    isPositive: true,
    subtitle: 'IATF 16949:2016'
  }, {
    title: 'ISO Compliance %',
    value: '95%',
    change: '+3%',
    isPositive: true,
    subtitle: 'ISO 9001:2015'
  }, {
    title: 'Documentation Completion %',
    value: '88%',
    change: '+8%',
    isPositive: true,
    subtitle: 'Required documents'
  }];
  // Certification Matrix Data
  const certificationMatrixData = [{
    certification: 'IATF 16949:2016',
    plant: 'Manesar',
    status: 'Certified',
    expiryDate: '2025-03-15',
    auditDate: '2024-12-15',
    score: 92,
    findings: 8
  }, {
    certification: 'IATF 16949:2016',
    plant: 'Chennai',
    status: 'Certified',
    expiryDate: '2025-06-20',
    auditDate: '2025-03-20',
    score: 95,
    findings: 5
  }, {
    certification: 'IATF 16949:2016',
    plant: 'Pune',
    status: 'Renewal Due',
    expiryDate: '2024-08-10',
    auditDate: '2024-05-10',
    score: 88,
    findings: 12
  }, {
    certification: 'IATF 16949:2016',
    plant: 'Bangalore',
    status: 'Certified',
    expiryDate: '2025-01-25',
    auditDate: '2024-10-25',
    score: 90,
    findings: 9
  }, {
    certification: 'ISO 9001:2015',
    plant: 'Manesar',
    status: 'Certified',
    expiryDate: '2025-04-10',
    auditDate: '2025-01-10',
    score: 94,
    findings: 6
  }, {
    certification: 'ISO 9001:2015',
    plant: 'Chennai',
    status: 'Certified',
    expiryDate: '2025-07-15',
    auditDate: '2025-04-15',
    score: 96,
    findings: 4
  }, {
    certification: 'ISO 9001:2015',
    plant: 'Pune',
    status: 'Certified',
    expiryDate: '2024-09-20',
    auditDate: '2024-06-20',
    score: 93,
    findings: 7
  }, {
    certification: 'ISO 9001:2015',
    plant: 'Bangalore',
    status: 'Certified',
    expiryDate: '2025-02-28',
    auditDate: '2024-11-28',
    score: 95,
    findings: 5
  }, {
    certification: 'ISO 14001:2015',
    plant: 'Manesar',
    status: 'Certified',
    expiryDate: '2025-05-12',
    auditDate: '2025-02-12',
    score: 91,
    findings: 8
  }, {
    certification: 'ISO 14001:2015',
    plant: 'Chennai',
    status: 'Certified',
    expiryDate: '2025-08-18',
    auditDate: '2025-05-18',
    score: 93,
    findings: 7
  }, {
    certification: 'ISO 14001:2015',
    plant: 'Pune',
    status: 'In Progress',
    expiryDate: '2024-10-05',
    auditDate: '2024-07-05',
    score: 85,
    findings: 15
  }, {
    certification: 'ISO 14001:2015',
    plant: 'Bangalore',
    status: 'Certified',
    expiryDate: '2025-03-22',
    auditDate: '2024-12-22',
    score: 92,
    findings: 8
  }, {
    certification: 'ISO 45001:2018',
    plant: 'Manesar',
    status: 'Certified',
    expiryDate: '2025-06-08',
    auditDate: '2025-03-08',
    score: 90,
    findings: 9
  }, {
    certification: 'ISO 45001:2018',
    plant: 'Chennai',
    status: 'Certified',
    expiryDate: '2025-09-14',
    auditDate: '2025-06-14',
    score: 94,
    findings: 6
  }, {
    certification: 'ISO 45001:2018',
    plant: 'Pune',
    status: 'Certified',
    expiryDate: '2024-11-30',
    auditDate: '2024-08-30',
    score: 89,
    findings: 10
  }, {
    certification: 'ISO 45001:2018',
    plant: 'Bangalore',
    status: 'Renewal Due',
    expiryDate: '2024-04-18',
    auditDate: '2024-01-18',
    score: 87,
    findings: 13
  }];
  // Compliance Trend Data
  const complianceTrendData = [{
    month: 'Jan',
    iatf: 85,
    iso9001: 88,
    iso14001: 82,
    iso45001: 84
  }, {
    month: 'Feb',
    iatf: 86,
    iso9001: 89,
    iso14001: 83,
    iso45001: 85
  }, {
    month: 'Mar',
    iatf: 87,
    iso9001: 90,
    iso14001: 85,
    iso45001: 86
  }, {
    month: 'Apr',
    iatf: 88,
    iso9001: 91,
    iso14001: 86,
    iso45001: 87
  }, {
    month: 'May',
    iatf: 89,
    iso9001: 92,
    iso14001: 88,
    iso45001: 88
  }, {
    month: 'Jun',
    iatf: 90,
    iso9001: 93,
    iso14001: 89,
    iso45001: 89
  }, {
    month: 'Jul',
    iatf: 91,
    iso9001: 94,
    iso14001: 90,
    iso45001: 90
  }, {
    month: 'Aug',
    iatf: 92,
    iso9001: 95,
    iso14001: 91,
    iso45001: 91
  }];
  // Audit Readiness Gauge Data
  const auditReadinessData = [{
    name: 'IATF 16949',
    value: 92,
    fill: '#3b82f6'
  }, {
    name: 'ISO 9001',
    value: 95,
    fill: '#10b981'
  }, {
    name: 'ISO 14001',
    value: 88,
    fill: '#f59e0b'
  }, {
    name: 'ISO 45001',
    value: 90,
    fill: '#8b5cf6'
  }];
  // Pending Documents List
  const pendingDocumentsData = [{
    id: 'DOC-2024-001',
    documentName: 'Process FMEA - Line 3 Assembly',
    certification: 'IATF 16949:2016',
    plant: 'Pune',
    category: 'Process Documentation',
    owner: 'Process Engineering',
    dueDate: '2024-02-15',
    status: 'In Progress',
    completionPercent: 75,
    priority: 'High',
    remarks: 'Risk analysis section pending review'
  }, {
    id: 'DOC-2024-002',
    documentName: 'Control Plan - Polymer Injection Molding',
    certification: 'IATF 16949:2016',
    plant: 'Manesar',
    category: 'Quality Planning',
    owner: 'Quality Team A',
    dueDate: '2024-02-10',
    status: 'In Progress',
    completionPercent: 85,
    priority: 'Critical',
    remarks: 'Awaiting customer approval'
  }, {
    id: 'DOC-2024-003',
    documentName: 'Environmental Aspects Register',
    certification: 'ISO 14001:2015',
    plant: 'Pune',
    category: 'Environmental',
    owner: 'EHS Team',
    dueDate: '2024-02-20',
    status: 'Not Started',
    completionPercent: 0,
    priority: 'High',
    remarks: 'New facility expansion documentation'
  }, {
    id: 'DOC-2024-004',
    documentName: 'Hazard Identification & Risk Assessment',
    certification: 'ISO 45001:2018',
    plant: 'Bangalore',
    category: 'Safety',
    owner: 'Safety Team',
    dueDate: '2024-02-08',
    status: 'Overdue',
    completionPercent: 60,
    priority: 'Critical',
    remarks: 'Risk assessment incomplete for chemical storage'
  }, {
    id: 'DOC-2024-005',
    documentName: 'Calibration Records - CMM Equipment',
    certification: 'IATF 16949:2016',
    plant: 'Chennai',
    category: 'Measurement System',
    owner: 'Quality Lab',
    dueDate: '2024-02-12',
    status: 'In Progress',
    completionPercent: 90,
    priority: 'Medium',
    remarks: 'External calibration pending certificate'
  }, {
    id: 'DOC-2024-006',
    documentName: 'Supplier Quality Manual Update',
    certification: 'IATF 16949:2016',
    plant: 'All Plants',
    category: 'Supplier Management',
    owner: 'Supply Chain',
    dueDate: '2024-02-25',
    status: 'In Progress',
    completionPercent: 50,
    priority: 'Medium',
    remarks: 'New supplier requirements being incorporated'
  }, {
    id: 'DOC-2024-007',
    documentName: 'Emergency Response Plan - Fire',
    certification: 'ISO 45001:2018',
    plant: 'Manesar',
    category: 'Safety',
    owner: 'EHS Team',
    dueDate: '2024-02-18',
    status: 'In Progress',
    completionPercent: 70,
    priority: 'High',
    remarks: 'Evacuation drill documentation pending'
  }, {
    id: 'DOC-2024-008',
    documentName: 'Waste Management Procedures',
    certification: 'ISO 14001:2015',
    plant: 'Chennai',
    category: 'Environmental',
    owner: 'Facility Management',
    dueDate: '2024-02-22',
    status: 'Not Started',
    completionPercent: 0,
    priority: 'Medium',
    remarks: 'New waste segregation process documentation'
  }, {
    id: 'DOC-2024-009',
    documentName: 'Internal Audit Schedule 2024',
    certification: 'ISO 9001:2015',
    plant: 'All Plants',
    category: 'Quality Management',
    owner: 'Quality Head',
    dueDate: '2024-02-05',
    status: 'Overdue',
    completionPercent: 40,
    priority: 'Critical',
    remarks: 'Q1 audit schedule needs finalization'
  }, {
    id: 'DOC-2024-010',
    documentName: 'Training Effectiveness Records',
    certification: 'IATF 16949:2016',
    plant: 'Bangalore',
    category: 'Human Resources',
    owner: 'HR & Training',
    dueDate: '2024-02-28',
    status: 'In Progress',
    completionPercent: 55,
    priority: 'Medium',
    remarks: 'Operator competency assessment pending'
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Certified':
        return 'bg-green-100 text-green-700';
      case 'Renewal Due':
        return 'bg-orange-100 text-orange-700';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Overdue':
        return 'bg-red-100 text-red-700';
      case 'Not Started':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-100 text-red-700';
      case 'High':
        return 'bg-orange-100 text-orange-700';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'Low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getScoreColor = (score: number) => {
    if (score >= 93) return '#10b981';
    if (score >= 88) return '#3b82f6';
    if (score >= 85) return '#f59e0b';
    return '#ef4444';
  };
  return <div data-id="element-1474">
      <DescriptionPanel title="Certification Compliance (IATF, ISO)" description="Comprehensive tracking of all certification compliance including IATF 16949, ISO 9001, ISO 14001, and ISO 45001 across all manufacturing plants. Monitor certification status, audit readiness, and documentation completion to ensure continuous compliance." insights={['ISO 9001 compliance leading at 95% - all plants maintaining strong quality systems', 'IATF readiness improved to 92% with focus on process documentation', 'Documentation completion at 88% - 10 pending documents requiring attention', '2 certifications flagged for renewal in next 60 days - Pune IATF and Bangalore ISO 45001']} data-id="element-1475" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-1476">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1477">
          Certification Compliance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1478">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-1479">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1480">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-1481">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-1482">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-1483">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-1484" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-1485" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Compliance Trend and Audit Readiness Gauge */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-1486">
        {/* Compliance Trend */}
        <Card className="p-6" data-id="element-1487">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1488">
            Compliance Trend
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1489">
            Compliance percentage across all certifications over time
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1490">
            <LineChart data={complianceTrendData} data-id="element-1491">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1492" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-1493" />
              <YAxis domain={[75, 100]} tick={{
              fontSize: 12
            }} data-id="element-1494" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1495" />
              <Legend data-id="element-1496" />
              <Line type="monotone" dataKey="iatf" stroke="#3b82f6" strokeWidth={3} name="IATF 16949" dot={{
              r: 4
            }} data-id="element-1497" />
              <Line type="monotone" dataKey="iso9001" stroke="#10b981" strokeWidth={3} name="ISO 9001" dot={{
              r: 4
            }} data-id="element-1498" />
              <Line type="monotone" dataKey="iso14001" stroke="#f59e0b" strokeWidth={3} name="ISO 14001" dot={{
              r: 4
            }} data-id="element-1499" />
              <Line type="monotone" dataKey="iso45001" stroke="#8b5cf6" strokeWidth={3} name="ISO 45001" dot={{
              r: 4
            }} data-id="element-1500" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1501">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-1502">
              <div className="text-center" data-id="element-1503">
                <div className="text-gray-600" data-id="element-1504">IATF 16949</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-1505">92%</div>
                <div className="text-xs text-gray-500" data-id="element-1506">+7% YoY</div>
              </div>
              <div className="text-center" data-id="element-1507">
                <div className="text-gray-600" data-id="element-1508">ISO 9001</div>
                <div className="text-lg font-bold text-green-600" data-id="element-1509">95%</div>
                <div className="text-xs text-gray-500" data-id="element-1510">+7% YoY</div>
              </div>
              <div className="text-center" data-id="element-1511">
                <div className="text-gray-600" data-id="element-1512">ISO 14001</div>
                <div className="text-lg font-bold text-orange-600" data-id="element-1513">91%</div>
                <div className="text-xs text-gray-500" data-id="element-1514">+9% YoY</div>
              </div>
              <div className="text-center" data-id="element-1515">
                <div className="text-gray-600" data-id="element-1516">ISO 45001</div>
                <div className="text-lg font-bold text-purple-600" data-id="element-1517">91%</div>
                <div className="text-xs text-gray-500" data-id="element-1518">+7% YoY</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Audit Readiness Gauge */}
        <Card className="p-6" data-id="element-1519">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1520">
            Audit Readiness Gauge
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1521">
            Current readiness level for upcoming certification audits
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1522">
            <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="90%" data={auditReadinessData} startAngle={180} endAngle={0} data-id="element-1523">
              <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} data-id="element-1524" />
              <RadialBar background dataKey="value" cornerRadius={10} label={{
              position: 'insideStart',
              fill: '#fff',
              fontSize: 14,
              fontWeight: 'bold'
            }} data-id="element-1525" />
              <Legend iconSize={10} layout="vertical" verticalAlign="middle" align="right" data-id="element-1526" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1527" />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1528">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-1529">
              <div className="text-center" data-id="element-1530">
                <div className="text-gray-600" data-id="element-1531">IATF</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-1532">92%</div>
                <div className="text-xs text-gray-500" data-id="element-1533">ready</div>
              </div>
              <div className="text-center" data-id="element-1534">
                <div className="text-gray-600" data-id="element-1535">ISO 9001</div>
                <div className="text-lg font-bold text-green-600" data-id="element-1536">95%</div>
                <div className="text-xs text-gray-500" data-id="element-1537">ready</div>
              </div>
              <div className="text-center" data-id="element-1538">
                <div className="text-gray-600" data-id="element-1539">ISO 14001</div>
                <div className="text-lg font-bold text-orange-600" data-id="element-1540">88%</div>
                <div className="text-xs text-gray-500" data-id="element-1541">ready</div>
              </div>
              <div className="text-center" data-id="element-1542">
                <div className="text-gray-600" data-id="element-1543">ISO 45001</div>
                <div className="text-lg font-bold text-purple-600" data-id="element-1544">90%</div>
                <div className="text-xs text-gray-500" data-id="element-1545">ready</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Certification Matrix */}
      <Card className="p-6 mb-8" data-id="element-1546">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-1547">
          Certification Matrix
        </h3>
        <div className="overflow-x-auto" data-id="element-1548">
          <table className="w-full" data-id="element-1549">
            <thead data-id="element-1550">
              <tr className="border-b-2 border-gray-300" data-id="element-1551">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1552">
                  Certification
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1553">
                  Plant
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1554">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1555">
                  Expiry Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1556">
                  Next Audit
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1557">
                  Score
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1558">
                  Findings
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1559">
              {certificationMatrixData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1560">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-1561">
                    {row.certification}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1562">
                    {row.plant}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1563">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-1564">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1565">
                    {row.expiryDate}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1566">
                    {row.auditDate}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1567">
                    <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold" style={{
                  backgroundColor: row.score >= 93 ? '#d1fae5' : row.score >= 88 ? '#dbeafe' : row.score >= 85 ? '#fef3c7' : '#fee2e2',
                  color: row.score >= 93 ? '#065f46' : row.score >= 88 ? '#1e40af' : row.score >= 85 ? '#92400e' : '#991b1b'
                }} data-id="element-1568">
                      {row.score}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm font-semibold text-gray-900" data-id="element-1569">
                    {row.findings}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-1570">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-1571">
            <div className="text-center" data-id="element-1572">
              <div className="text-gray-600" data-id="element-1573">Total Certifications</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-1574">16</div>
              <div className="text-xs text-gray-500" data-id="element-1575">across all plants</div>
            </div>
            <div className="text-center" data-id="element-1576">
              <div className="text-gray-600" data-id="element-1577">Certified</div>
              <div className="text-xl font-bold text-green-600" data-id="element-1578">13</div>
              <div className="text-xs text-gray-500" data-id="element-1579">active</div>
            </div>
            <div className="text-center" data-id="element-1580">
              <div className="text-gray-600" data-id="element-1581">Renewal Due</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-1582">2</div>
              <div className="text-xs text-gray-500" data-id="element-1583">next 60 days</div>
            </div>
            <div className="text-center" data-id="element-1584">
              <div className="text-gray-600" data-id="element-1585">In Progress</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-1586">1</div>
              <div className="text-xs text-gray-500" data-id="element-1587">ongoing</div>
            </div>
            <div className="text-center" data-id="element-1588">
              <div className="text-gray-600" data-id="element-1589">Avg Score</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-1590">91.4</div>
              <div className="text-xs text-gray-500" data-id="element-1591">overall</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Pending Documents List */}
      <Card className="p-6" data-id="element-1592">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-1593">
          Pending Documents List
        </h3>
        <div className="overflow-x-auto" data-id="element-1594">
          <table className="w-full" data-id="element-1595">
            <thead data-id="element-1596">
              <tr className="border-b-2 border-gray-300" data-id="element-1597">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1598">
                  Document ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1599">
                  Document Name
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1600">
                  Certification
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1601">
                  Plant
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1602">
                  Category
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1603">
                  Owner
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1604">
                  Due Date
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1605">
                  Status
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1606">
                  Completion %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1607">
                  Priority
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1608">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1609">
              {pendingDocumentsData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1610">
                  <td className="py-3 px-4 text-sm font-medium text-blue-600" data-id="element-1611">
                    {row.id}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-800 max-w-xs" data-id="element-1612">
                    {row.documentName}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1613">
                    {row.certification}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1614">
                    {row.plant}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1615">
                    {row.category}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1616">
                    {row.owner}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1617">
                    {row.dueDate}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1618">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-1619">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4" data-id="element-1620">
                    <div className="flex items-center" data-id="element-1621">
                      <div className="flex-1" data-id="element-1622">
                        <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1623">
                          <div className="h-full bg-blue-600 rounded-full" style={{
                        width: `${row.completionPercent}%`
                      }} data-id="element-1624" />
                        </div>
                      </div>
                      <span className="ml-2 text-xs font-semibold text-gray-700" data-id="element-1625">
                        {row.completionPercent}%
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1626">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getPriorityColor(row.priority)}`} data-id="element-1627">
                      {row.priority}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-1628">
                    {row.remarks}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-1629">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-1630">
            <div className="text-center" data-id="element-1631">
              <div className="text-gray-600" data-id="element-1632">Total Pending</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-1633">10</div>
              <div className="text-xs text-gray-500" data-id="element-1634">documents</div>
            </div>
            <div className="text-center" data-id="element-1635">
              <div className="text-gray-600" data-id="element-1636">Critical</div>
              <div className="text-xl font-bold text-red-600" data-id="element-1637">2</div>
              <div className="text-xs text-gray-500" data-id="element-1638">priority</div>
            </div>
            <div className="text-center" data-id="element-1639">
              <div className="text-gray-600" data-id="element-1640">Overdue</div>
              <div className="text-xl font-bold text-red-600" data-id="element-1641">2</div>
              <div className="text-xs text-gray-500" data-id="element-1642">delayed</div>
            </div>
            <div className="text-center" data-id="element-1643">
              <div className="text-gray-600" data-id="element-1644">In Progress</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-1645">6</div>
              <div className="text-xs text-gray-500" data-id="element-1646">ongoing</div>
            </div>
            <div className="text-center" data-id="element-1647">
              <div className="text-gray-600" data-id="element-1648">Not Started</div>
              <div className="text-xl font-bold text-gray-600" data-id="element-1649">2</div>
              <div className="text-xs text-gray-500" data-id="element-1650">pending</div>
            </div>
            <div className="text-center" data-id="element-1651">
              <div className="text-gray-600" data-id="element-1652">Avg Completion</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-1653">56%</div>
              <div className="text-xs text-gray-500" data-id="element-1654">overall</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default CertificationComplianceDashboard;